import React, { useContext, useEffect } from 'react';
import useAsyncEffect from 'use-async-effect';
import Inputs from 'components/inputs/inputs';
import { Form } from 'react-bootstrap';
import Helpers from 'utils/helpers';
import BoardingContext from 'components/navigation/boarding/boardingContext';
import BoardingHelper from 'components/navigation/boarding/boardingHelper';
import BoardingService from 'services/boardingService';
import UserService from 'services/userService';

export default function BoardingStoreInformation(props) {
  const { boardingState } = useContext(BoardingContext);
  const step = boardingState.step;

  if (step === BoardingHelper.enums.steps.StoreKeyEntry) {
    return <StoreKeyEntryForm onStoreSearch={props.onStoreSearch} />;
  }
  if (step === BoardingHelper.enums.steps.StoreSearch) {
    return <StoreSearch />;
  }
  if (step === BoardingHelper.enums.steps.StoreDetails) {
    return <StoreDetailsEntryForm />;
  }
  if (step === BoardingHelper.enums.steps.StoreError) {
    return <StoreErrorMessage />;
  }
  if (step === BoardingHelper.enums.steps.StoreErrorReporting) {
    return <StoreErrorReporting />;
  }

  return null;
}

function StoreKeyEntryForm(props) {
  const { boardingState, setStoreSearch } = useContext(BoardingContext);

  return (
    <Form.Group>
      <Form.Label required>POSitouch Key Number</Form.Label>
      <Inputs.Integer required placeholder='Please enter key number' value={boardingState.store.search} onValueChange={setStoreSearch} onEnterPress={props.onStoreSearch} autoFocus='true' />
    </Form.Group>
  );
}

function StoreSearch(props) {
  const { boardingState, setStep, updateStore } = useContext(BoardingContext);

  const decodePhone = (phone) => {
    if (phone && Helpers.Object.isString(phone)) {
      return phone.replace('(', '').replace(')', '');
    }
    return null;
  };

  useAsyncEffect(async () => {
    try {
      const keyDetails = await BoardingService.API.getKeyDetails(boardingState.data.dealer?.id || -1, boardingState.store.search);
      if (keyDetails) {
        if (keyDetails.isOnCloud) {
          setStep(BoardingHelper.enums.steps.StoreError, {
            store: {
              search: boardingState.store.search,
              error: 'A site has been already boarded using this key number',
            },
          });
        }
        else if (!keyDetails.isActive) {
          setStep(BoardingHelper.enums.steps.StoreError, {
            store: {
              search: boardingState.store.search,
              error: 'Key number has been marked inactive',
            },
          });
        }
        else {
          updateStore({
            keyId: boardingState.store.search,
            licenseKeyId: boardingState.store.search,
            name: keyDetails.customerName,
            number: '1',
            phone: decodePhone(keyDetails.customerPhone),
            address1: null,
            address2: null,
            city: null,
            state: null,
            zip: null,
          });
          setStep(BoardingHelper.enums.steps.StoreDetails);
        }
      }
    }
    catch (error) {
      if (error.errorCode === 1) {
        //key not found
        setStep(BoardingHelper.enums.steps.StoreError, {
          store: {
            search: boardingState.store.search,
            error: 'Key number not found',
          },
        });
      }
      else if (error.errorCode === 2) {
        //key beongs to another dealer
        setStep(BoardingHelper.enums.steps.StoreError, {
          store: {
            search: boardingState.store.search,
            error: 'Key number belongs to another dealer',
          },
        });
      }
    }
  }, []);

  return (
    <div>Searching...</div>
  );
}

function StoreDetailsEntryForm(props) {
  const { boardingState, updateStore } = useContext(BoardingContext);
  const data = boardingState.data.store || {};

  const updateField = (field, value) => {
    let newData = { ...data };
    newData[field] = value;
    updateStore(newData);
  };

  return (
    <>
      <Form.Group>
        <Form.Label>POSitouch Key Number</Form.Label>
        <Inputs.Plaintext value={data.keyId} />
      </Form.Group>
      <Form.Row>
        <Form.Group className='col-md-8'>
          <Form.Label required>Store Name</Form.Label>
          <Inputs.Text required placeholder='Please enter store name' value={data.name} onValueChange={(v) => updateField('name', v)} />
        </Form.Group>
        <Form.Group className='col-md-4 mb-3'>
          <Form.Label>Store Number</Form.Label>
          <Inputs.Text placeholder='Please enter store number' value={data.number} onValueChange={(v) => updateField('number', v)} />
        </Form.Group>
      </Form.Row>
      <Form.Group>
        <Form.Label>Phone</Form.Label>
        <Inputs.Phone placeholder='Please enter store phone' value={data.phone} onValueChange={(v) => updateField('phone', v)} />
      </Form.Group>
      <Form.Group>
        <Form.Label>Address</Form.Label>
        <Inputs.Text placeholder='Please enter street address' value={data.address1} onValueChange={(v) => updateField('address1', v)} />
      </Form.Group>
      <Form.Group>
        <Form.Label>Address 2</Form.Label>
        <Inputs.Text placeholder='Please enter address line 2' value={data.address2} onValueChange={(v) => updateField('address2', v)} />
      </Form.Group>
      <Form.Row>
        <Form.Group className='col-md-6'>
          <Form.Label>City</Form.Label>
          <Inputs.Text placeholder='Please enter city' value={data.city} onValueChange={(v) => updateField('city', v)} />
        </Form.Group>
        <Form.Group className='col-md-4'>
          <Form.Label>State</Form.Label>
          <Inputs.Text placeholder='Please enter state' value={data.state} onValueChange={(v) => updateField('state', v)} />
        </Form.Group>
        <Form.Group className='col-md-2'>
          <Form.Label>Zip</Form.Label>
          <Inputs.Text placeholder='Please enter zip' value={data.zip} onValueChange={(v) => updateField('zip', v)} />
        </Form.Group>
      </Form.Row>
    </>
  );
}

function StoreErrorMessage(props) {
  const { boardingState } = useContext(BoardingContext);

  return (
    <>
      <div className='alert alert-danger'>
        <div className='alert-heading'>
          <i className='icons8-box-important align-middle' style={{ fontSize: 30 }} />
          <span className='h4 align-middle ml-2'>There was a problem</span>
        </div>
      </div>
      <div className='mb-2'>
        <div><div className='form-label'>POSitouch Key Number</div></div>
        <div className='mb-2'>{boardingState.store.search}</div>
        <div><div className='form-label'>Error</div></div>
        <div className='mb-2'>{boardingState.store.error}</div>
      </div>
    </>
  );
}

function StoreErrorReporting(props) {
  const { boardingState, updateProblem } = useContext(BoardingContext);
  const data = boardingState.problem || {};

  const updateField = (field, value) => {
    let newData = { ...data };
    newData[field] = value;
    updateProblem(newData);
  };

  useEffect(() => {
    updateProblem({
      email: data.email || UserService.getUserEmail(),
      details: `[Error]\nMessage: ${boardingState.store.error}\nStep: Store Information\n\n[Entered Data]\nDealer Name: ${boardingState.data.dealer.name}\nDealer Id: ${boardingState.data.dealer.id}\nKey Number: ${boardingState.store.search}`,
      comments: '',
    });
  }, []);

  return (
    <>
      <div className='mb-2'>
        <div><div className='form-label'>Problem Details</div></div>
        <div className='card card-light-default card-body mt-1 mb-3'>
          <pre className='m-0'>
            {data.details}
          </pre>
        </div>
        <Form.Group>
          <Form.Label>Report To</Form.Label>
          <Inputs.Plaintext value='POSitouch Cloud Support' />
        </Form.Group>
        <Form.Group>
          <Form.Label required>Report From</Form.Label>
          <Inputs.Text
            required
            placeholder='Please enter your email address'
            value={data.email}
            onValueChange={(v) => updateField('email', v)}
            help='We will respond to this email address with a resolution'
          />
        </Form.Group>
        <Form.Group>
          <Form.Label>Comments</Form.Label>
          <Inputs.TextArea placeholder='Please enter any additional comments' value={data.comments} onValueChange={(v) => updateField('comments', v)} />
        </Form.Group>
      </div>
    </>
  );
}